import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, KeyRound, Check, Video } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const [step, setStep] = useState('email'); // 'email', 'code', 'done'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [displayCode, setDisplayCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleRequestCode = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      if (res.data.code) {
        setDisplayCode(res.data.code);
        setStep('code');
        toast.success('Reset code generated!');
      } else {
        toast.info('If an account exists, a reset code will appear');
      }
    } catch (e) {
      toast.error('Something went wrong. Try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!code || !newPassword) return;
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email,
        code,
        new_password: newPassword,
      });
      setStep('done');
      toast.success('Password reset successfully!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid or expired code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white font-inter flex items-center justify-center p-6 relative overflow-hidden">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[#D4AF37]/[0.05] rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-violet-900/[0.08] rounded-full blur-[140px]" />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <Link to="/login" className="flex items-center gap-2 text-zinc-500 hover:text-white mb-8 transition-colors text-sm" data-testid="back-to-login-link">
          <ArrowLeft className="w-4 h-4" />
          Back to sign in
        </Link>

        {step === 'email' && (
          <div className="backdrop-blur-2xl bg-black/60 border border-white/[0.08] rounded-2xl p-8 md:p-10 shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
            <div className="w-12 h-12 bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-xl flex items-center justify-center mb-5">
              <Mail className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37]/80 mb-2">Recover access</p>
            <h1 className="font-satoshi font-bold text-2xl tracking-tight mb-2">Forgot password?</h1>
            <p className="text-zinc-500 text-sm mb-6">Enter your email and we'll generate a reset code.</p>

            <form onSubmit={handleRequestCode} className="space-y-5">
              <div>
                <label className="block text-xs uppercase tracking-wider font-semibold text-zinc-400 mb-2">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  data-testid="reset-email-input"
                  className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#D4AF37]/50 focus:ring-1 focus:ring-[#D4AF37]/30 transition-all"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="request-code-btn"
                className="w-full py-3.5 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black font-semibold rounded-full hover:shadow-[0_0_30px_rgba(212,175,55,0.35)] transition-all disabled:opacity-50"
              >
                {loading ? 'Sending…' : 'Get reset code'}
              </button>
            </form>
          </div>
        )}

        {step === 'code' && (
          <div className="backdrop-blur-2xl bg-black/60 border border-white/[0.08] rounded-2xl p-8 md:p-10 shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
            <div className="w-12 h-12 bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-xl flex items-center justify-center mb-5">
              <KeyRound className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37]/80 mb-2">Reset code</p>
            <h1 className="font-satoshi font-bold text-2xl tracking-tight mb-5">Enter your code</h1>

            <div className="bg-[#D4AF37]/[0.08] border border-[#D4AF37]/20 rounded-xl p-5 mb-6 text-center">
              <p className="text-xs uppercase tracking-wider text-[#D4AF37]/80 mb-2">Your reset code</p>
              <p className="text-3xl font-mono font-bold tracking-[0.3em] text-white" data-testid="display-code">{displayCode}</p>
              <p className="text-xs text-zinc-500 mt-2">Expires in 10 minutes</p>
            </div>

            <form onSubmit={handleResetPassword} className="space-y-5">
              <div>
                <label className="block text-xs uppercase tracking-wider font-semibold text-zinc-400 mb-2">Enter the code above</label>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="6-digit code"
                  maxLength={6}
                  required
                  data-testid="reset-code-input"
                  className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-4 py-3 text-white text-center text-xl tracking-[0.3em] font-mono placeholder:text-zinc-600 focus:outline-none focus:border-[#D4AF37]/50 focus:ring-1 focus:ring-[#D4AF37]/30 transition-all"
                />
              </div>

              <div>
                <label className="block text-xs uppercase tracking-wider font-semibold text-zinc-400 mb-2">New password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  required
                  minLength={6}
                  data-testid="new-password-input"
                  className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#D4AF37]/50 focus:ring-1 focus:ring-[#D4AF37]/30 transition-all"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="reset-password-btn"
                className="w-full py-3.5 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black font-semibold rounded-full hover:shadow-[0_0_30px_rgba(212,175,55,0.35)] transition-all disabled:opacity-50"
              >
                {loading ? 'Resetting…' : 'Reset password'}
              </button>
            </form>
          </div>
        )}

        {step === 'done' && (
          <div className="backdrop-blur-2xl bg-black/60 border border-white/[0.08] rounded-2xl p-8 md:p-10 text-center shadow-[0_8px_32px_rgba(0,0,0,0.4)]">
            <div className="w-16 h-16 bg-gradient-to-br from-[#D4AF37]/20 to-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-5">
              <Check className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#D4AF37]/80 mb-2">Success</p>
            <h1 className="font-satoshi font-bold text-2xl tracking-tight mb-2">Password reset</h1>
            <p className="text-zinc-500 mb-6 text-sm">Your password has been updated successfully.</p>
            <button
              onClick={() => navigate('/login')}
              data-testid="go-to-login-btn"
              className="w-full py-3.5 bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black font-semibold rounded-full hover:shadow-[0_0_30px_rgba(212,175,55,0.35)] transition-all"
            >
              Sign in
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
